import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock } from 'lucide-react';

const AppointmentPaymentCallbackScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const status = searchParams.get('status');
  const transactionId = searchParams.get('transactionId');
  const clientRefRaw = searchParams.get('order_number') || searchParams.get('clientReference') || localStorage.getItem('pending_hubtel_order');
  const appointmentId = clientRefRaw ? clientRefRaw.split('-retry-')[0] : null;

  // Clear it so it doesn't linger for future unrelated visits
  useEffect(() => {
    if (clientRefRaw && localStorage.getItem('pending_hubtel_order')) {
      localStorage.removeItem('pending_hubtel_order');
    }
  }, [clientRefRaw]);

  const [verificationStatus, setVerificationStatus] = useState('VERIFYING'); // VERIFYING, SUCCESS, FAILED, PENDING
  const [countdown, setCountdown] = useState(5);

  const isSuccessParams = status === 'Success' || status === 'Completed' || status?.toLowerCase() === 'success' || status?.toLowerCase() === 'paid';

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 6; // 30 seconds max
    const interval = 5000;
    let timerId = null;

    const checkStatus = async () => {
      try {
        if (!appointmentId) {
           setVerificationStatus(isSuccessParams ? 'SUCCESS' : 'FAILED');
           return;
        }

        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const res = await fetch(`${API_BASE}/api/store/payments/hubtel_status/?client_reference=${clientRefRaw}`);
        const data = await res.json();

        if (data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
          const txData = data.data[0];
          const txStatus = txData.TransactionStatus?.toString().toLowerCase();

          if (txStatus === 'success') {
            setVerificationStatus('SUCCESS');
            return;
          } else if (txStatus === 'failed') {
            setVerificationStatus('FAILED');
            return;
          }
        }
        
        // Still pending or unknown response code
        attempts++;
        if (attempts >= maxAttempts) {
          setVerificationStatus('PENDING');
        } else {
          timerId = setTimeout(checkStatus, interval);
        }
      } catch (err) {
        attempts++;
        if (attempts >= maxAttempts) {
          setVerificationStatus('PENDING');
        } else {
          timerId = setTimeout(checkStatus, interval);
        }
      }
    };

    checkStatus();

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [appointmentId, isSuccessParams]);

  useEffect(() => {
    if (verificationStatus === 'SUCCESS') {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        navigate('/my-bookings');
      }
    }
  }, [verificationStatus, countdown, navigate]);

  if (verificationStatus === 'VERIFYING') {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center py-24 px-4 text-center font-sans">
        <div className="max-w-md bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-neutral-200 flex flex-col items-center">
          <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 font-semibold uppercase tracking-wider">Verifying payment with Hubtel...</p>
          <p className="text-sm text-gray-400 mt-2">Please do not close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center py-24 px-4 text-center font-sans">
      <div className="max-w-md bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-neutral-200 flex flex-col items-center">
        {verificationStatus === 'SUCCESS' && <CheckCircle size={80} className="text-green-500 mb-6" />}
        {verificationStatus === 'FAILED' && <XCircle size={80} className="text-red-500 mb-6" />}
        {verificationStatus === 'PENDING' && <Clock size={80} className="text-yellow-500 mb-6" />}
        
        <h1 className="text-2xl font-bold uppercase tracking-widest mb-4">
          {verificationStatus === 'SUCCESS' && 'Payment Successful'}
          {verificationStatus === 'FAILED' && 'Payment Failed'}
          {verificationStatus === 'PENDING' && 'Payment Processing'}
        </h1>
        
        <p className="text-neutral-600 mb-8 max-w-sm text-sm">
          {verificationStatus === 'SUCCESS' && 'Thank you for your payment. Your appointment booking has been confirmed.'}
          {verificationStatus === 'FAILED' && 'Unfortunately, your payment could not be completed. Please try booking again or contact support.'}
          {verificationStatus === 'PENDING' && 'Your payment is taking longer than usual to confirm. We are processing it in the background. Please check your bookings later to see the updated status.'}
        </p>

        {transactionId && (
          <p className="text-xs text-neutral-400 mb-8 uppercase tracking-widest">
            TXN: {transactionId}
          </p>
        )}

        <div className="flex flex-col w-full gap-4">
          <Link 
            to="/my-bookings"
            className="w-full bg-black text-white px-6 py-4 text-xs font-bold tracking-widest uppercase hover:bg-neutral-900 transition-colors text-center rounded-lg"
          >
            {verificationStatus === 'SUCCESS' ? `View Bookings (${countdown}s)` : 'View Bookings'}
          </Link>
          {verificationStatus !== 'SUCCESS' && (
            <Link 
              to="/bookings"
              className="w-full bg-white text-black border border-neutral-200 px-6 py-4 text-xs font-bold tracking-widest uppercase hover:bg-neutral-50 hover:border-black transition-colors text-center rounded-lg"
            >
              Book Again
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentPaymentCallbackScreen;
